import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useScope } from "@state/scopeContext.lib";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { ClientActionsDropdown } from "@components/organizationView/clients/ClientActionsDropdown/ClientActionsDropdown";
import { EntityActionsDropdown } from "@components/organizationView/clients/EntityActionsDropdown/EntityActionsDropdown";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { closeOverviewRowToClientRow } from "../closeOverviewRowToClientRow";
import { ClientsCloseOverviewRow } from "../clientsCloseOverviewViewConfig";
import styles from "./CloseActionCell.module.scss";

export const CloseActionCell = ({
    row,
}: CellContext<ViewTableFeatures, ClientsCloseOverviewRow, unknown>) => {
    const { changeActiveWorkspace } = useScope();

    if (row.original.rowType === "entity") {
        const workspace = row.getParentRow()?.original;
        const entityId = row.original.entityId;
        const workspaceId = row.original.workspaceId ?? workspace?.workspaceId;

        if (entityId == null || !workspaceId) {
            return null;
        }

        return (
            <ViewTableCell
                captureClicks
                className={classNames(tableStyles.showOnHover, styles.actions)}
            >
                <EntityActionsDropdown
                    entity={{
                        id: entityId,
                        name: row.original.name,
                        members:
                            workspace?.accessMembers ??
                            workspace?.members ??
                            [],
                        isBusiness: row.original.isBusiness === true,
                    }}
                    clientId={workspaceId}
                />
            </ViewTableCell>
        );
    }

    if (row.original.rowType !== "workspace") {
        return null;
    }

    // Row id is "workspace:<uuid>" — strip the prefix
    const workspaceId = row.original.id.replace("workspace:", "");
    const clientRow = closeOverviewRowToClientRow(row.original);

    return (
        <ViewTableCell captureClicks className={styles.actions}>
            <RowExpansion row={row}>
                {(isExpanded) => (
                    <Button
                        variant="primary"
                        size="sm"
                        className={classNames({
                            [tableStyles.showOnHover]: !isExpanded,
                        })}
                        onClick={() =>
                            changeActiveWorkspace(
                                workspaceId,
                                OrganizationMemberClientAccessSource.CLIENTS_LIST,
                            )
                        }
                    >
                        Open
                    </Button>
                )}
            </RowExpansion>
            <div className={tableStyles.showOnHover}>
                <ClientActionsDropdown client={clientRow} />
            </div>
        </ViewTableCell>
    );
};
